/**
 * 日期区间条件：查询表单 / 列头过滤 / 高级筛选 的唯一对账入口。
 *
 * 同一字段只有一个区间值。查询表单与列头请求用 field_ge + field_le，
 * 高级筛选用 operator=between 且值为 "start;end"。对账后这两套写法必须一致。
 */

export type DateRangeSyncSource = 'form' | 'column' | 'moreQuery' | 'tag';

const IGNORED_PARAM_KEYS = new Set([
  'current',
  'size',
  'paginationSize',
  'orderList',
  'moreQueryConfigs',
  'paramsRel',
  'sPageCode_',
]);

const QUERY_OPERATOR_SUFFIXES = [
  'multiLike', 'notLike', 'startWith', 'endWith',
  'isNotEmpty', 'isEmpty', 'notNull', 'isNull',
  'notIn', 'between', 'like', 'eq', 'neq', 'gte', 'lte',
  'in', 'ge', 'le', 'gt', 'lt', 'nq',
];

export interface DateRangeValue {
  start: string;
  end: string;
}

const hasValue = (value: any) => value !== undefined && value !== null && value !== '';

/**
 * 解析查询参数键。表别名/表名前缀必须保留：
 * mes_sales_out_order.createTime_ge → fieldName=mes_sales_out_order.createTime, operator=ge
 * 若先按 '.' 截成 createTime_ge，后续按 createTime_ge 读写，会丢掉原参数。
 */
export function parseQueryParamField(paramKey: string): { fieldName: string; operator: string } {
  const fullKey = String(paramKey || '');
  const dotParts = fullKey.split('.');
  const lastPart = dotParts[dotParts.length - 1] || '';
  for (const operator of QUERY_OPERATOR_SUFFIXES) {
    const suffixToken = `_${operator}`;
    if (lastPart.endsWith(suffixToken) && lastPart.length > suffixToken.length) {
      const lastField = lastPart.slice(0, -suffixToken.length);
      const fieldName = dotParts.length > 1
        ? [...dotParts.slice(0, -1), lastField].join('.')
        : lastField;
      return {fieldName, operator};
    }
  }
  return {fieldName: fullKey, operator: 'like'};
}

export function getBareQueryFieldName(fieldName: string): string {
  return String(fieldName || '').split('.').pop() || '';
}

export function isSameQueryField(a: string, b: string): boolean {
  if (!a || !b) {
    return false;
  }
  if (a === b) {
    return true;
  }
  return getBareQueryFieldName(a) === getBareQueryFieldName(b);
}

export function preferPrefixedQueryField(a: string, b: string): string {
  if (a && a.includes('.')) {
    return a;
  }
  if (b && b.includes('.')) {
    return b;
  }
  return b || a;
}

function matchQueryFieldKey(key: string | undefined, fieldName: string): boolean {
  if (!key) {
    return false;
  }
  return isSameQueryField(key, fieldName) || isSameQueryField(parseQueryParamField(key).fieldName, fieldName);
}

export function formatDateRangePart(value: any): string {
  if (!hasValue(value)) {
    return '';
  }
  if (value && typeof value.format === 'function') {
    return value.format('YYYY-MM-DD');
  }
  return String(value).trim();
}

export function parseDateRangeParts(value: any): DateRangeValue | null {
  if (!hasValue(value)) {
    return null;
  }
  if (Array.isArray(value)) {
    const start = formatDateRangePart(value[0]);
    const end = formatDateRangePart(value[1]) || start;
    return start ? {start, end} : null;
  }
  const text = formatDateRangePart(value);
  if (!text) {
    return null;
  }
  const parts = text.split(';').map((item) => item.trim()).filter(Boolean);
  if (!parts.length) {
    return null;
  }
  return {start: parts[0], end: parts[1] || parts[0]};
}

export function formatDateRangeValue(range: DateRangeValue | null | undefined): string {
  if (!range?.start) {
    return '';
  }
  return `${range.start};${range.end || range.start}`;
}

function parseMoreQueryConfigs(raw: any): { searchField: any[]; logicOperator?: string } | null {
  let configs = raw;
  if (typeof configs === 'string') {
    try {
      configs = JSON.parse(configs);
    } catch {
      return null;
    }
  }
  if (!configs || !Array.isArray(configs.searchField)) {
    return null;
  }
  return configs;
}

function readRangeFromMoreQuery(configs: any, fieldName: string): DateRangeValue | null {
  const parsed = parseMoreQueryConfigs(configs);
  const item = parsed?.searchField?.find((fieldJson: any) => {
    return matchQueryFieldKey(fieldJson?.key, fieldName);
  });
  if (!item) {
    return null;
  }
  // 仅认 between：多选 in 的值也用分号拼接，不能凭 ';' 当成日期区间
  if (item.operator === 'between') {
    return parseDateRangeParts(item.value);
  }
  return null;
}

function readRangeFromFlat(params: Record<string, any> | null | undefined, fieldName: string): DateRangeValue | null {
  if (!params) {
    return null;
  }
  const geExact = formatDateRangePart(params[`${fieldName}_ge`]);
  const leExact = formatDateRangePart(params[`${fieldName}_le`]);
  if (geExact || leExact) {
    return {start: geExact || leExact, end: leExact || geExact};
  }
  const betweenExact = parseDateRangeParts(params[`${fieldName}_between`]);
  if (betweenExact) {
    return betweenExact;
  }
  const plainExact = parseDateRangeParts(params[fieldName]);
  if (plainExact) {
    return plainExact;
  }

  let ge = '';
  let le = '';
  let between: DateRangeValue | null = null;
  let plain: DateRangeValue | null = null;
  Object.keys(params).forEach((key) => {
    if (IGNORED_PARAM_KEYS.has(key)) {
      return;
    }
    const parsed = parseQueryParamField(key);
    if (!isSameQueryField(parsed.fieldName, fieldName)) {
      return;
    }
    if (parsed.operator === 'ge') {
      ge = formatDateRangePart(params[key]);
    } else if (parsed.operator === 'le') {
      le = formatDateRangePart(params[key]);
    } else if (parsed.operator === 'between') {
      between = parseDateRangeParts(params[key]);
    } else if (parsed.operator === 'like') {
      plain = parseDateRangeParts(params[key]);
    }
  });
  if (ge || le) {
    return {start: ge || le, end: le || ge};
  }
  return between || plain;
}

function collectDateFieldNames(
  params: Record<string, any>,
  triggerParams?: Record<string, any> | null
): string[] {
  const names = new Set<string>();
  const addFrom = (source?: Record<string, any> | null) => {
    if (!source) {
      return;
    }
    Object.keys(source).forEach((key) => {
      if (IGNORED_PARAM_KEYS.has(key)) {
        return;
      }
      const {fieldName, operator} = parseQueryParamField(key);
      if (operator === 'ge' || operator === 'le' || operator === 'between') {
        names.add(fieldName);
      }
    });
    const configs = parseMoreQueryConfigs(source.moreQueryConfigs);
    configs?.searchField?.forEach((fieldJson: any) => {
      // 仅认 between：多选 in 的值也用分号拼接，不能凭 ';' 当成日期区间
      if (fieldJson?.operator === 'between') {
        names.add(parseQueryParamField(fieldJson.key).fieldName);
      }
    });
  };
  addFrom(params);
  addFrom(triggerParams);
  return canonicalizeDateFieldNames([...names]);
}

function canonicalizeDateFieldNames(names: string[]): string[] {
  return names.filter((name) => {
    const prefixedSibling = names.find(
      (other) => other !== name && isSameQueryField(other, name) && other.includes('.')
    );
    return !prefixedSibling || name.includes('.');
  });
}

function moreQueryHasField(configs: any, fieldName: string): boolean {
  const parsed = parseMoreQueryConfigs(configs);
  return !!parsed?.searchField?.some((fieldJson: any) => matchQueryFieldKey(fieldJson?.key, fieldName));
}

function flatHasDateField(params: Record<string, any> | null | undefined, fieldName: string): boolean {
  if (!params) {
    return false;
  }
  if ([`${fieldName}_ge`, `${fieldName}_le`, `${fieldName}_between`, fieldName]
    .some((key) => Object.prototype.hasOwnProperty.call(params, key))) {
    return true;
  }
  return Object.keys(params).some((key) => {
    if (IGNORED_PARAM_KEYS.has(key)) {
      return false;
    }
    const parsed = parseQueryParamField(key);
    return isSameQueryField(parsed.fieldName, fieldName)
      && (parsed.operator === 'ge' || parsed.operator === 'le' || parsed.operator === 'between' || key === fieldName);
  });
}

function removeDateFieldFlatParams(params: Record<string, any>, fieldName: string) {
  Object.keys(params).forEach((key) => {
    if (IGNORED_PARAM_KEYS.has(key)) {
      return;
    }
    const parsed = parseQueryParamField(key);
    if (!isSameQueryField(parsed.fieldName, fieldName) && key !== fieldName) {
      return;
    }
    if (parsed.operator === 'ge' || parsed.operator === 'le' || parsed.operator === 'between' || key === fieldName) {
      delete params[key];
    }
  });
}

function upsertMoreQueryBetween(params: Record<string, any>, fieldName: string, range: DateRangeValue | null) {
  const configs = parseMoreQueryConfigs(params.moreQueryConfigs);
  if (!configs) {
    return;
  }
  const existingIndex = configs.searchField.findIndex((fieldJson: any) => {
    return matchQueryFieldKey(fieldJson?.key, fieldName);
  });
  if (!range) {
    if (existingIndex > -1) {
      configs.searchField.splice(existingIndex, 1);
    }
  } else {
    const nextItem = {
      key: preferPrefixedQueryField(
        existingIndex > -1 ? configs.searchField[existingIndex].key : '',
        fieldName
      ),
      value: formatDateRangeValue(range),
      operator: 'between',
      type: 'STRING',
    };
    if (existingIndex > -1) {
      configs.searchField[existingIndex] = {
        ...configs.searchField[existingIndex],
        ...nextItem,
      };
    } else {
      configs.searchField.push(nextItem);
    }
  }
  if (!configs.searchField.length) {
    delete params.moreQueryConfigs;
  } else {
    params.moreQueryConfigs = configs;
  }
}

function resolveExistingDateFieldName(
  params: Record<string, any>,
  triggerParams: Record<string, any> | null | undefined,
  fieldName: string
): string {
  const sources = [triggerParams, params];
  for (const source of sources) {
    if (!source) {
      continue;
    }
    for (const key of Object.keys(source)) {
      if (IGNORED_PARAM_KEYS.has(key)) {
        continue;
      }
      const parsed = parseQueryParamField(key);
      if (
        isSameQueryField(parsed.fieldName, fieldName)
        && (parsed.operator === 'ge' || parsed.operator === 'le' || parsed.operator === 'between')
        && parsed.fieldName.includes('.')
      ) {
        return parsed.fieldName;
      }
    }
  }
  return fieldName;
}

function writeDateRange(params: Record<string, any>, fieldName: string, range: DateRangeValue | null) {
  removeDateFieldFlatParams(params, fieldName);
  if (range?.start) {
    params[`${fieldName}_ge`] = range.start;
    params[`${fieldName}_le`] = range.end || range.start;
  }
  upsertMoreQueryBetween(params, fieldName, range?.start ? range : null);
}

function pickLastWriterRange(
  fieldName: string,
  source: DateRangeSyncSource,
  params: Record<string, any>,
  triggerParams?: Record<string, any> | null
): DateRangeValue | null {
  const triggerMore = triggerParams?.moreQueryConfigs;
  const triggerReplace = (source === 'moreQuery' || source === 'tag') && triggerMore !== undefined;

  if (source === 'moreQuery' || source === 'tag') {
    if (triggerReplace) {
      if (moreQueryHasField(triggerMore, fieldName)) {
        return readRangeFromMoreQuery(triggerMore, fieldName) || readRangeFromFlat(triggerParams, fieldName);
      }
      if (flatHasDateField(triggerParams, fieldName)) {
        return readRangeFromFlat(triggerParams, fieldName);
      }
      return null;
    }
    return readRangeFromMoreQuery(params.moreQueryConfigs, fieldName)
      || readRangeFromFlat(triggerParams, fieldName)
      || readRangeFromFlat(params, fieldName);
  }

  if (flatHasDateField(triggerParams, fieldName)) {
    return readRangeFromFlat(triggerParams, fieldName);
  }
  return readRangeFromMoreQuery(params.moreQueryConfigs, fieldName) || readRangeFromFlat(params, fieldName);
}

/**
 * 合并结束后调用：让日期区间的 _ge/_le 与高级筛选 between 指向同一值。
 */
export function reconcileDateRangeParams(
  params: Record<string, any>,
  source: DateRangeSyncSource,
  triggerParams?: Record<string, any> | null
) {
  if (!params) {
    return params;
  }
  const configs = parseMoreQueryConfigs(params.moreQueryConfigs);
  if (configs) {
    params.moreQueryConfigs = configs;
  }
  collectDateFieldNames(params, triggerParams).forEach((fieldName) => {
    const range = pickLastWriterRange(fieldName, source, params, triggerParams);
    const writeField = preferPrefixedQueryField(
      resolveExistingDateFieldName(params, triggerParams, fieldName),
      fieldName
    );
    writeDateRange(params, writeField, range);
  });
  return params;
}

export type QuerySyncEcho = {
  source: DateRangeSyncSource;
  remainingAfterInit: number;
};

export function beginQuerySyncEcho(gridTableStore: any, gridKey: string, source: DateRangeSyncSource) {
  const tableObj = gridTableStore?.getGridObjByKey?.(gridKey);
  if (!tableObj) {
    return;
  }
  tableObj.querySyncEcho = {source, remainingAfterInit: 1} as QuerySyncEcho;
}

export function peekQuerySyncEcho(tableObj: any): QuerySyncEcho | null {
  return tableObj?.querySyncEcho || null;
}

export function consumeAfterInitQuerySyncEcho(tableObj: any): QuerySyncEcho | null {
  const echo = tableObj?.querySyncEcho as QuerySyncEcho | undefined;
  if (!echo) {
    return null;
  }
  echo.remainingAfterInit -= 1;
  if (echo.remainingAfterInit <= 0) {
    tableObj.querySyncEcho = null;
  }
  return echo;
}

export function shouldSkipQueryFormEcho(echo: QuerySyncEcho | null | undefined) {
  return echo?.source === 'form';
}

export function shouldSkipColQueryEcho(echo: QuerySyncEcho | null | undefined) {
  return echo?.source === 'column';
}

export function shouldSkipMoreQueryEcho(echo: QuerySyncEcho | null | undefined) {
  return echo?.source === 'moreQuery' || echo?.source === 'tag';
}

/**
 * flushGrid 的 afterInit 是否跳过全部 UI 回写。
 * 筛选面板/标签 AND 在 flush 前已用完整合并结果 syncGridQueryComponents；
 * flush 的是线格式（仅 moreQueryConfigs），再回写会把查询表单等清空。
 */
export function shouldSkipAfterInitUiEcho(echo: QuerySyncEcho | null | undefined) {
  return echo?.source === 'moreQuery' || echo?.source === 'tag';
}
